import { S3_ERROR_CODES, S3_ERROR_MESSAGES, type S3ErrorResponse, type S3ErrorDetails } from '@app/types/s3Error'

/**
 * Парсит XML ответ с ошибкой от S3 и извлекает информацию об ошибке
 */
export const parseS3ErrorFromXml = (xmlResponse: string): S3ErrorResponse | null => {
  try {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlResponse, 'text/xml')

    const parseError = xmlDoc.querySelector('parsererror')
    if (parseError) {
      return {
        code: 'MalformedXML',
        message: 'Не удалось разобрать XML ответ от S3'
      }
    }

    const code = xmlDoc.querySelector('Code')?.textContent?.trim()
    const message = xmlDoc.querySelector('Message')?.textContent?.trim()
    const requestId = xmlDoc.querySelector('RequestId')?.textContent?.trim()
    const hostId = xmlDoc.querySelector('HostId')?.textContent?.trim()

    if (!code || !message) {
      return {
        code: 'UnknownError',
        message: 'Неизвестная ошибка S3'
      }
    }

    return {
      code,
      message,
      requestId,
      hostId
    }
  } catch (error) {
    return {
      code: 'ParseError',
      message: 'Ошибка при парсинге ответа от S3'
    }
  }
}

/**
 * Создает детальную информацию об ошибке S3 с пользовательским сообщением
 */
export const createS3ErrorDetails = (status: number, s3Error: S3ErrorResponse): S3ErrorDetails => {
  const userMessage = S3_ERROR_MESSAGES[s3Error.code] || s3Error.message

  return {
    status,
    code: s3Error.code,
    message: s3Error.message,
    userMessage,
    requestId: s3Error.requestId,
    hostId: s3Error.hostId
  }
}

/**
 * Определяет, является ли HTTP статус код ошибкой S3
 */
export const isS3ErrorStatus = (status: number): boolean => {
  return status >= 300 && status < 600
}

/**
 * Определяет, является ли ошибка S3 критической (требует повторной попытки)
 */
export const isS3ErrorRetryable = (s3Error: S3ErrorResponse): boolean => {
  const retryableCodes = [
    S3_ERROR_CODES.INTERNAL_ERROR,
    S3_ERROR_CODES.SERVICE_UNAVAILABLE,
    S3_ERROR_CODES.SLOW_DOWN,
    S3_ERROR_CODES.REQUEST_TIMEOUT,
    S3_ERROR_CODES.TOO_MANY_REQUESTS
  ]

  return retryableCodes.includes(s3Error.code as any)
}

/**
 * Определяет, является ли ошибка S3 связанной с аутентификацией
 */
export const isS3AuthError = (s3Error: S3ErrorResponse): boolean => {
  const authErrorCodes = [
    S3_ERROR_CODES.ACCESS_DENIED,
    S3_ERROR_CODES.INVALID_ACCESS_KEY_ID,
    S3_ERROR_CODES.INVALID_SECURITY,
    S3_ERROR_CODES.EXPIRED_TOKEN,
    S3_ERROR_CODES.INVALID_TOKEN,
    S3_ERROR_CODES.TOKEN_REFRESH_REQUIRED,
    S3_ERROR_CODES.SIGNATURE_DOES_NOT_MATCH,
    S3_ERROR_CODES.REQUEST_TIME_TOO_SKEWED
  ]

  return authErrorCodes.includes(s3Error.code as any)
}

/**
 * Определяет, является ли ошибка S3 связанной с размером файла
 */
export const isS3FileSizeError = (s3Error: S3ErrorResponse): boolean => {
  const fileSizeErrorCodes = [
    S3_ERROR_CODES.ENTITY_TOO_LARGE,
    S3_ERROR_CODES.ENTITY_TOO_SMALL,
    S3_ERROR_CODES.INLINE_DATA_TOO_LARGE,
    S3_ERROR_CODES.CLOUD_TOTAL_ALIVE_SIZE_QUOTA_EXCEED,
    S3_ERROR_CODES.BUCKET_MAX_SIZE_EXCEEDED
  ]

  return fileSizeErrorCodes.includes(s3Error.code as any)
}
